import { useEffect, useState, useRef, useCallback } from "react";
import { useAuth } from "../App";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Video, Users, Clock, Award, ChevronLeft, Menu, X, Calendar, Sparkles, Heart, Star, CreditCard, Check, BookOpen, Utensils, Brain, FileText, ArrowUp, ArrowLeft, MessageCircle, Phone, Inbox, LogOut, Lock } from "lucide-react";
import { Button } from "../components/ui/button";
import ReviewsSection from "../components/ReviewsSection";

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

/* ── Countdown Timer Component ── */
const SESSIONS = [
  { id: 's1', label: 'الجلسة الأولى', prefixed: 'للجلسة الأولى', shortLabel: 'الأولى', date: '٣٠ أيار', target: new Date('2026-05-30T15:00:00Z') },
  { id: 's2', label: 'الجلسة الثانية', prefixed: 'للجلسة الثانية', shortLabel: 'الثانية', date: '٣١ أيار', target: new Date('2026-05-31T15:00:00Z') },
  { id: 's3', label: 'الجلسة الثالثة', prefixed: 'للجلسة الثالثة', shortLabel: 'الثالثة', date: '٦ حزيران', target: new Date('2026-06-06T15:00:00Z') },
  { id: 's4', label: 'الجلسة الرابعة', prefixed: 'للجلسة الرابعة', shortLabel: 'الرابعة', date: '٧ حزيران', target: new Date('2026-06-07T15:00:00Z') },
];

const TIMEZONES = [
  { id: 'gmt', label: 'غرينتش', labelShort: 'GMT', offset: 0, time: '٣:٠٠ م' },
  { id: 'syria', label: 'سوريا', labelShort: 'دمشق', offset: 3, time: '٦:٠٠ م' },
  { id: 'mecca', label: 'مكة المكرمة', labelShort: 'مكة', offset: 3, time: '٦:٠٠ م' },
  { id: 'dubai', label: 'دبي', labelShort: 'دبي', offset: 4, time: '٧:٠٠ م' },
  { id: 'algeria', label: 'الجزائر', labelShort: 'الجزائر', offset: 1, time: '٤:٠٠ م' },
];

const FlipUnit = ({ value, label }) => {
  const strVal = String(value).padStart(2, '0');
  const prevRef = useRef(strVal);
  const [flipping, setFlipping] = useState(false);

  useEffect(() => {
    if (prevRef.current !== strVal) {
      setFlipping(true);
      const t = setTimeout(() => setFlipping(false), 500);
      prevRef.current = strVal;
      return () => clearTimeout(t);
    }
  }, [strVal]);

  return (
    <div className="flex flex-col items-center gap-1.5">
      <div className={`relative w-16 h-20 sm:w-20 sm:h-24 md:w-24 md:h-28 rounded-xl overflow-hidden shadow-lg ${flipping ? 'animate-countdown-flip' : ''}`}>
        {/* Card background */}
        <div className="absolute inset-0 bg-gradient-to-b from-[#1A1D3D] to-[#0F1129]"></div>
        {/* Divider line */}
        <div className="absolute top-1/2 left-0 right-0 h-px bg-white/10 z-10"></div>
        {/* Number */}
        <div className="relative w-full h-full flex items-center justify-center">
          <span className="text-3xl sm:text-4xl md:text-5xl font-black text-transparent bg-clip-text bg-gradient-to-b from-white to-white/70" style={{ fontFamily: 'Cairo, sans-serif', fontVariantNumeric: 'tabular-nums' }}>
            {strVal}
          </span>
        </div>
        {/* Shine overlay */}
        <div className="absolute inset-x-0 top-0 h-1/2 bg-gradient-to-b from-white/[0.08] to-transparent pointer-events-none"></div>
        {/* Side accents */}
        <div className="absolute left-0 top-2 bottom-2 w-px bg-gradient-to-b from-transparent via-[#7C3AED]/40 to-transparent"></div>
        <div className="absolute right-0 top-2 bottom-2 w-px bg-gradient-to-b from-transparent via-[#EC4899]/40 to-transparent"></div>
      </div>
      <span className="text-[10px] sm:text-xs font-semibold text-[#8B92B3] uppercase tracking-wider">{label}</span>
    </div>
  );
};

const CountdownTimer = () => {
  const [activeSession, setActiveSession] = useState('s1');
  const [activeTz, setActiveTz] = useState('gmt');
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  const [now, setNow] = useState(new Date());

  const activeSessionData = SESSIONS.find(s => s.id === activeSession) || SESSIONS[0];

  const calcTimeLeft = useCallback(() => {
    const diff = activeSessionData.target.getTime() - Date.now();
    if (diff <= 0) return { days: 0, hours: 0, minutes: 0, seconds: 0 };
    return {
      days: Math.floor(diff / (1000 * 60 * 60 * 24)),
      hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
      minutes: Math.floor((diff / (1000 * 60)) % 60),
      seconds: Math.floor((diff / 1000) % 60),
    };
  }, [activeSessionData]);

  useEffect(() => {
    setTimeLeft(calcTimeLeft());
    const interval = setInterval(() => {
      setTimeLeft(calcTimeLeft());
      setNow(new Date());
    }, 1000);
    return () => clearInterval(interval);
  }, [calcTimeLeft]);

  const activeTzData = TIMEZONES.find(t => t.id === activeTz);
  const localHours = (now.getUTCHours() + (activeTzData?.offset || 0)) % 24;
  const localMins = now.getUTCMinutes();
  const localTime = `${String(localHours).padStart(2, '0')}:${String(localMins).padStart(2, '0')}`;
  const isExpired = timeLeft.days === 0 && timeLeft.hours === 0 && timeLeft.minutes === 0 && timeLeft.seconds === 0 && activeSessionData.target.getTime() < Date.now();

  return (
    <section className="py-16 md:py-24 px-4 md:px-12 lg:px-24 relative overflow-hidden" data-testid="countdown-section">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#0F1129] via-[#1A1D3D] to-[#2D1B69]"></div>
      <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGNpcmNsZSBjeD0iMzAiIGN5PSIzMCIgcj0iMiIgZmlsbD0icmdiYSgyNTUsMjU1LDI1NSwwLjAzKSIvPjwvc3ZnPg==')] opacity-60"></div>
      {/* Glow orbs */}
      <div className="absolute top-0 right-1/4 w-96 h-96 bg-[#7C3AED]/20 rounded-full blur-[120px] pointer-events-none"></div>
      <div className="absolute bottom-0 left-1/4 w-96 h-96 bg-[#EC4899]/15 rounded-full blur-[120px] pointer-events-none"></div>

      <div className="relative max-w-5xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm rounded-full px-4 py-1.5 mb-4 border border-white/10">
            <span className="w-2 h-2 bg-[#10B981] rounded-full animate-pulse"></span>
            <span className="text-xs font-semibold text-white/70 tracking-wider uppercase" data-testid="countdown-session-label">العد التنازلي {activeSessionData.prefixed}</span>
          </div>
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-white mb-2" style={{ fontFamily: 'Cairo, sans-serif' }}>
            {isExpired ? 'بدأت الجلسة!' : 'تبدأ خلال'}
          </h2>
        </div>

        {/* Session Tabs */}
        <div className="flex justify-center mb-10">
          <div className="inline-flex items-center bg-white/5 backdrop-blur-sm rounded-2xl p-1.5 border border-white/10 gap-1 overflow-x-auto max-w-full" data-testid="session-tabs">
            {SESSIONS.map((s) => (
              <button
                key={s.id}
                onClick={() => setActiveSession(s.id)}
                className={`flex flex-col items-center px-3 sm:px-5 py-2 sm:py-2.5 rounded-xl text-xs sm:text-sm font-semibold transition-all duration-300 whitespace-nowrap ${
                  activeSession === s.id
                    ? 'bg-gradient-to-r from-[#7C3AED] to-[#EC4899] text-white shadow-lg shadow-purple-500/30'
                    : 'text-white/50 hover:text-white/80 hover:bg-white/5'
                }`}
                data-testid={`session-tab-${s.id}`}
              >
                <span>{s.shortLabel}</span>
                <span className={`text-[10px] sm:text-[11px] font-normal mt-0.5 ${activeSession === s.id ? 'text-white/80' : 'text-white/40'}`}>{s.date}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Main Countdown */}
        <div className="flex justify-center items-center gap-3 sm:gap-4 md:gap-6 mb-10">
          <FlipUnit value={timeLeft.days} label="يوم" />
          <div className="flex flex-col gap-3 pt-2">
            <div className="w-2 h-2 rounded-full bg-gradient-to-br from-[#7C3AED] to-[#EC4899] animate-pulse"></div>
            <div className="w-2 h-2 rounded-full bg-gradient-to-br from-[#EC4899] to-[#F97316] animate-pulse"></div>
          </div>
          <FlipUnit value={timeLeft.hours} label="ساعة" />
          <div className="flex flex-col gap-3 pt-2">
            <div className="w-2 h-2 rounded-full bg-gradient-to-br from-[#7C3AED] to-[#EC4899] animate-pulse"></div>
            <div className="w-2 h-2 rounded-full bg-gradient-to-br from-[#EC4899] to-[#F97316] animate-pulse"></div>
          </div>
          <FlipUnit value={timeLeft.minutes} label="دقيقة" />
          <div className="flex flex-col gap-3 pt-2">
            <div className="w-2 h-2 rounded-full bg-gradient-to-br from-[#7C3AED] to-[#EC4899] animate-pulse"></div>
            <div className="w-2 h-2 rounded-full bg-gradient-to-br from-[#EC4899] to-[#F97316] animate-pulse"></div>
          </div>
          <FlipUnit value={timeLeft.seconds} label="ثانية" />
        </div>

        {/* Timezone Tabs */}
        <div className="flex justify-center mb-6">
          <div className="inline-flex items-center bg-white/5 backdrop-blur-sm rounded-2xl p-1.5 border border-white/10 gap-1 overflow-x-auto max-w-full">
            {TIMEZONES.map((tz) => (
              <button
                key={tz.id}
                onClick={() => setActiveTz(tz.id)}
                className={`px-3 sm:px-5 py-2 sm:py-2.5 rounded-xl text-xs sm:text-sm font-semibold transition-all duration-300 whitespace-nowrap ${
                  activeTz === tz.id
                    ? 'bg-gradient-to-r from-[#7C3AED] to-[#EC4899] text-white shadow-lg shadow-purple-500/30'
                    : 'text-white/50 hover:text-white/80 hover:bg-white/5'
                }`}
                data-testid={`tz-tab-${tz.id}`}
              >
                {tz.labelShort}
              </button>
            ))}
          </div>
        </div>

        {/* Active Timezone Info */}
        <div className="flex justify-center">
          <div className="bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10 p-5 sm:p-6 max-w-md w-full">
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-white/50 text-xs mb-0.5">المنطقة الزمنية</p>
                <p className="text-white font-bold text-base sm:text-lg" style={{ fontFamily: 'Cairo, sans-serif' }}>بتوقيت {activeTzData?.label}</p>
              </div>
              <div className="text-left">
                <p className="text-white/50 text-xs mb-0.5">الوقت الحالي</p>
                <p className="text-xl sm:text-2xl font-black text-transparent bg-clip-text bg-gradient-to-r from-[#7C3AED] to-[#EC4899]" style={{ fontVariantNumeric: 'tabular-nums' }}>{localTime}</p>
              </div>
            </div>
            <div className="h-px bg-gradient-to-r from-transparent via-white/20 to-transparent mb-4"></div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-[#7C3AED]" />
                <span className="text-white/70 text-sm" data-testid="countdown-session-date">{activeSessionData.date}</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-[#EC4899]" />
                <span className="text-white font-bold text-sm">{activeTzData?.time}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const LandingPage = () => {
  const { user, login, logout, checkAuth } = useAuth();
  const navigate = useNavigate();
  const [courseInfo, setCourseInfo] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [paypalLoading, setPaypalLoading] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  // Fetch unread Zoom-link count for enrolled users
  useEffect(() => {
    if (!user?.enrolled) { setUnreadCount(0); return; }
    let cancelled = false;
    const fetchUnread = async () => {
      try {
        const res = await axios.get(`${API}/messages/unread-count`, { withCredentials: true });
        if (!cancelled) setUnreadCount(res.data?.unread_count || res.data?.count || 0);
      } catch (e) {
        // silent — just leaves badge hidden
      }
    };
    fetchUnread();
    const id = setInterval(fetchUnread, 30000); // refresh every 30s
    return () => { cancelled = true; clearInterval(id); };
  }, [user?.enrolled, user?.user_id]);

  useEffect(() => {
    const fetchCourseInfo = async () => {
      try {
        const response = await axios.get(`${API}/course-info`);
        setCourseInfo(response.data);
      } catch (error) {
        console.error("Failed to fetch course info:", error);
      }
    };
    fetchCourseInfo();
  }, []);

  // Handle return from Stripe Checkout (success_url points to home) and PayPal approval return
  useEffect(() => {
    const url = new URL(window.location.href);
    const payment = url.searchParams.get("payment");
    const sessionId = url.searchParams.get("session_id");
    const paypalToken = url.searchParams.get("token"); // PayPal returns ?token=ORDER_ID&PayerID=...
    if (!payment) return;

    window.history.replaceState({}, "", "/");

    if (payment === "cancelled") {
      toast.message("تم إلغاء عملية الدفع.");
      return;
    }

    // Stripe success flow
    if (payment === "success" && sessionId) {
      const toastId = toast.loading("جارٍ التحقق من عملية الدفع...");
      let attempts = 0;
      const maxAttempts = 6;
      const pollInterval = 2500;
      const poll = async () => {
        attempts += 1;
        try {
          const res = await axios.get(`${API}/payments/status/${sessionId}`, { withCredentials: true });
          const { payment_status, status } = res.data;
          if (payment_status === "paid") {
            toast.success("🎉 تم تأكيد دفعتك وتسجيلك في الدورة!", { id: toastId, duration: 6000 });
            await checkAuth();
            return;
          }
          if (status === "expired" || payment_status === "failed") {
            toast.error("انتهت جلسة الدفع أو فشلت. حاول مجدداً.", { id: toastId });
            return;
          }
          if (attempts >= maxAttempts) {
            toast.message("جارٍ معالجة الدفع — سيصلك التأكيد خلال دقائق في صندوق الوارد.", { id: toastId });
            return;
          }
          setTimeout(poll, pollInterval);
        } catch (e) {
          console.error("Payment polling failed:", e);
          toast.error("تعذّر التحقق من الدفع.", { id: toastId });
        }
      };
      poll();
      return;
    }

    // PayPal capture flow
    if (payment === "paypal_success" && paypalToken) {
      const toastId = toast.loading("جارٍ تأكيد دفعتك عبر PayPal...");
      (async () => {
        try {
          const res = await axios.post(
            `${API}/payments/paypal/capture/${paypalToken}`,
            {},
            { withCredentials: true }
          );
          if (res.data?.payment_status === "paid") {
            toast.success("🎉 تم تأكيد دفعتك وتسجيلك في الدورة!", { id: toastId, duration: 6000 });
            await checkAuth();
          } else {
            toast.error("لم يتم تأكيد الدفع بعد. حاول مجدداً.", { id: toastId });
          }
        } catch (e) {
          console.error("PayPal capture failed:", e);
          toast.error(e?.response?.data?.detail || "تعذّر تأكيد دفعة PayPal.", { id: toastId });
        }
      })();
    }
  }, [checkAuth]);

  const handleEnrollClick = async () => {
    if (!user) {
      toast.message("سجّل دخولك أولاً لإتمام عملية الدفع");
      login();
      return;
    }
    setCheckoutLoading(true);
    try {
      const res = await axios.post(
        `${API}/payments/checkout`,
        { origin_url: window.location.origin },
        { withCredentials: true }
      );
      if (res.data?.url) {
        window.location.href = res.data.url;
      } else {
        toast.error("تعذّر بدء عملية الدفع. حاول مجدداً.");
        setCheckoutLoading(false);
      }
    } catch (e) {
      console.error("Checkout failed:", e);
      toast.error(e?.response?.data?.detail || "حدث خطأ أثناء الدفع. حاول مجدداً.");
      setCheckoutLoading(false);
    }
  };

  const handlePayPalClick = async () => {
    if (!user) {
      toast.message("سجّل دخولك أولاً لإتمام عملية الدفع");
      login();
      return;
    }
    setPaypalLoading(true);
    try {
      const res = await axios.post(
        `${API}/payments/paypal/create-order`,
        { origin_url: window.location.origin },
        { withCredentials: true }
      );
      if (res.data?.url) {
        window.location.href = res.data.url;
      } else {
        toast.error("تعذّر بدء عملية الدفع عبر PayPal. حاول مجدداً.");
        setPaypalLoading(false);
      }
    } catch (e) {
      console.error("PayPal checkout failed:", e);
      toast.error(e?.response?.data?.detail || "حدث خطأ أثناء الدفع عبر PayPal.");
      setPaypalLoading(false);
    }
  };

  const features = [
    {
      icon: <Utensils className="w-6 h-6" />,
      title: "فهم الأكل العاطفي",
      description: "اكتشف الروابط الخفية بين مشاعرك وعلاقتك بالطعام"
    },
    {
      icon: <Video className="w-6 h-6" />,
      title: "جلسات مباشرة عبر Zoom",
      description: "٤ جلسات تفاعلية مباشرة"
    },
    {
      icon: <Brain className="w-6 h-6" />,
      title: "وعي عميق",
      description: "فتح طبقات الوعي لفهم الرابط بين المشاعر والجسد"
    },
    {
      icon: <FileText className="w-6 h-6" />,
      title: "كُتيب تطبيقي بعد الكورس",
      description: "دليل عملي يُرافقك في رحلتك ويعمّق فهمك بعد كل جلسة"
    }
  ];

  const sessionDates = [
    { date: "٣٠ أيار", day: "السبت" },
    { date: "٣١ أيار", day: "الأحد" },
    { date: "٦ حزيران", day: "السبت" },
    { date: "٧ حزيران", day: "الأحد" }
  ];

  return (
    <div className="min-h-screen bg-[#FAFBFF] overflow-x-hidden relative" data-testid="landing-page">
      {/* Decorative Blobs - hidden on mobile */}
      <div className="decorative-blob-1 hidden md:block" style={{ top: '-100px', left: '-100px' }}></div>
      <div className="decorative-blob-2 hidden md:block" style={{ bottom: '200px', right: '-150px' }}></div>
      
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 glass border-b border-[#E2E8F0]">
        <div className="max-w-7xl mx-auto px-4 md:px-12 lg:px-24">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-2">
              <div className="w-9 h-9 bg-gradient-to-br from-[#7C3AED] to-[#EC4899] rounded-xl flex items-center justify-center shadow-lg shadow-purple-500/20">
                <Sparkles className="w-5 h-5 text-white" />
              </div>
              <span className="font-bold text-[#1A1D3D]" style={{ fontFamily: 'Cairo, sans-serif' }}>
                وعي الغذاء صلاة
              </span>
            </div>

            {/* Desktop Nav */}
            <nav className="hidden md:flex items-center gap-6 lg:gap-8">
              <a href="#course" className="text-[#4A5078] hover:text-[#7C3AED] transition-colors font-medium">الدورة</a>
              <a href="#pricing" className="text-[#4A5078] hover:text-[#7C3AED] transition-colors font-medium">الاستثمار</a>
              {user ? (
                <>
                  <button
                    onClick={() => navigate('/dashboard')}
                    className="relative flex items-center gap-2 text-[#4A5078] hover:text-[#7C3AED] transition-colors font-medium"
                    data-testid="nav-inbox-button"
                  >
                    <Inbox className="w-4 h-4" />
                    <span>روابط Zoom</span>
                  </button>
                  <div className="flex items-center gap-3 pl-3 border-r border-[#E2E8F0] pr-4">
                    {user.picture ? (
                      <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full ring-2 ring-[#7C3AED]/30" />
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#7C3AED] to-[#EC4899] flex items-center justify-center text-white text-xs font-bold">
                        {user.name?.[0] || 'U'}
                      </div>
                    )}
                    <span className="text-sm font-medium text-[#1A1D3D] max-w-[120px] truncate" data-testid="nav-user-name">{user.name}</span>
                    <button
                      onClick={logout}
                      className="text-[#8B92B3] hover:text-[#EF4444] transition-colors"
                      title="تسجيل الخروج"
                      data-testid="nav-logout-button"
                    >
                      <LogOut className="w-4 h-4" />
                    </button>
                  </div>
                </>
              ) : (
                <Button 
                  onClick={login}
                  className="bg-gradient-to-r from-[#7C3AED] to-[#EC4899] hover:from-[#6D28D9] hover:to-[#DB2777] text-white shadow-lg shadow-purple-500/25"
                  data-testid="login-button"
                >
                  تسجيل الدخول
                </Button>
              )}
            </nav>

            {/* Mobile Menu Toggle */}
            <button 
              className="md:hidden p-2"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-toggle"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-[#E2E8F0] py-4 px-4">
            <nav className="flex flex-col gap-4">
              <a href="#course" className="text-[#4A5078] hover:text-[#7C3AED] transition-colors py-2 font-medium" onClick={() => setMobileMenuOpen(false)}>الدورة</a>
              <a href="#pricing" className="text-[#4A5078] hover:text-[#7C3AED] transition-colors py-2 font-medium" onClick={() => setMobileMenuOpen(false)}>الاستثمار</a>
              {user ? (
                <>
                  <button
                    onClick={() => { setMobileMenuOpen(false); navigate('/dashboard'); }}
                    className="flex items-center gap-2 text-[#4A5078] hover:text-[#7C3AED] transition-colors py-2 font-medium text-right"
                    data-testid="mobile-nav-inbox-button"
                  >
                    <Inbox className="w-4 h-4" />
                    <span>روابط Zoom</span>
                  </button>
                  <div className="flex items-center gap-3 py-2 border-t border-[#E2E8F0] mt-2 pt-3">
                    {user.picture ? (
                      <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full ring-2 ring-[#7C3AED]/30" />
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#7C3AED] to-[#EC4899] flex items-center justify-center text-white text-xs font-bold">
                        {user.name?.[0] || 'U'}
                      </div>
                    )}
                    <span className="text-sm font-medium text-[#1A1D3D] flex-1 truncate">{user.name}</span>
                    <button
                      onClick={() => { setMobileMenuOpen(false); logout(); }}
                      className="text-[#8B92B3] hover:text-[#EF4444] transition-colors p-2"
                      data-testid="mobile-nav-logout-button"
                    >
                      <LogOut className="w-5 h-5" />
                    </button>
                  </div>
                </>
              ) : (
                <Button 
                  onClick={login}
                  className="bg-gradient-to-r from-[#7C3AED] to-[#EC4899] hover:from-[#6D28D9] hover:to-[#DB2777] text-white w-full"
                  data-testid="mobile-login-button"
                >
                  تسجيل الدخول
                </Button>
              )}
            </nav>
          </div>
        )}
      </header>

      {/* Hero Section */}
      <section 
        id="hero"
        className="relative pt-32 pb-20 md:pt-48 md:pb-40 px-4 md:px-12 lg:px-24 overflow-hidden"
        style={{
          background: 'linear-gradient(135deg, #FAFBFF 0%, #F0F4FF 50%, #FCE7F3 100%)'
        }}
      >
        <div className="hero-overlay absolute inset-0"></div>
        <div className="relative max-w-7xl mx-auto">
          <div className="max-w-3xl mx-auto animate-fade-in text-center">
            {/* Persistent enrollment confirmation — visible whenever enrolled user is on home */}
            {user?.enrolled && (
              <>
                <button
                  type="button"
                  onClick={() => navigate('/dashboard')}
                  className="group relative inline-flex items-center gap-3 mb-6 px-5 py-3 rounded-2xl bg-gradient-to-r from-[#10B981] to-[#059669] text-white shadow-xl shadow-emerald-500/30 hover:shadow-emerald-500/40 hover:scale-[1.02] transition-all duration-300"
                  data-testid="enrolled-banner"
                >
                  {unreadCount > 0 && (
                    <>
                      <span className="absolute -top-2 -left-2 min-w-[26px] h-[26px] px-1.5 bg-[#EF4444] text-white rounded-full text-xs font-black flex items-center justify-center shadow-lg ring-2 ring-white z-10 animate-bounce">
                        {unreadCount > 99 ? '99+' : unreadCount}
                      </span>
                      <span className="absolute -top-2 -left-2 w-[26px] h-[26px] bg-[#EF4444] rounded-full animate-ping opacity-75"></span>
                    </>
                  )}
                  <div className="relative flex items-center justify-center w-9 h-9 rounded-full bg-white/20">
                    <Check className="w-5 h-5 text-white" strokeWidth={3} />
                  </div>
                  <div className="flex flex-col items-start text-right" style={{ fontFamily: 'Cairo, sans-serif' }}>
                    <span className="text-sm md:text-base font-extrabold leading-tight">
                      أنت مُسجّل في الدورة
                    </span>
                    {unreadCount > 0 && (
                      <span className="text-[11px] md:text-xs font-medium opacity-90 leading-tight">
                        لديك {unreadCount > 99 ? '99+' : unreadCount} رابط Zoom جديد — اضغط للعرض
                      </span>
                    )}
                  </div>
                  <Inbox className="w-5 h-5 text-white/80 group-hover:text-white transition" />
                </button>

                {/* Telegram group join — for enrolled (paid) users */}
                <div className="mb-6">
                  <a
                    href="https://t.me/+THgHVe-b9EJjNzhk"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group inline-flex items-center gap-3 px-6 py-4 rounded-2xl bg-gradient-to-r from-[#0088CC] to-[#005A87] text-white shadow-xl shadow-sky-500/30 hover:shadow-sky-500/50 hover:scale-[1.03] active:scale-[0.98] transition-all duration-300"
                    data-testid="telegram-group-button"
                  >
                    <div className="flex items-center justify-center w-12 h-12 rounded-full bg-white/20 ring-2 ring-white/30">
                      {/* Inline Telegram SVG */}
                      <svg viewBox="0 0 24 24" className="w-7 h-7 fill-white" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                        <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
                      </svg>
                    </div>
                    <div className="flex flex-col items-start text-right" style={{ fontFamily: 'Cairo, sans-serif' }}>
                      <span className="text-[10px] md:text-xs font-bold uppercase tracking-wider opacity-90 leading-none mb-1">
                        مجموعة الكورس على تلغرام
                      </span>
                      <span className="text-base md:text-lg font-extrabold leading-tight">
                        انضم إلى مجموعة التلغرام الخاصة بالكورس
                      </span>
                    </div>
                    <ArrowLeft className="w-5 h-5 text-white/80 group-hover:text-white group-hover:-translate-x-1 transition" />
                  </a>
                </div>

                {/* Free Booklet — vertical book opening/closing animation */}
                <div className="mb-8 flex flex-col items-center gap-3">
                  <button
                    type="button"
                    onClick={() => navigate('/dashboard?tab=booklet')}
                    className="group relative outline-none focus:ring-4 focus:ring-amber-300/40 rounded-lg"
                    data-testid="booklet-cta-landing"
                    style={{ fontFamily: 'Cairo, sans-serif' }}
                    aria-label="إضغط هنا للحصول على الكتيب التطبيقي"
                  >
                    {/* 3D scene wrapper */}
                    <div className="booklet-scene">
                      {/* Book (stays vertical) */}
                      <div className="booklet-book">
                        {/* Inside pages (revealed when cover opens) */}
                        <div className="booklet-pages">
                          <div className="booklet-page-content">
                            <BookOpen className="w-6 h-6 text-[#92400E] mb-1" strokeWidth={2.2} />
                            <p className="text-[10px] font-bold text-[#92400E] leading-tight">كتيب</p>
                            <p className="text-[10px] font-bold text-[#92400E] leading-tight">تطبيقي</p>
                            <div className="mt-1 w-8 h-px bg-[#92400E]/40"></div>
                            <p className="text-[8px] text-[#A16207] mt-1 leading-tight">هدية مجانية</p>
                          </div>
                        </div>

                        {/* Cover (rotates open/close on the spine) */}
                        <div className="booklet-cover">
                          <div className="booklet-cover-face">
                            <span className="text-[9px] font-bold tracking-widest text-amber-200 uppercase">هدية</span>
                            <span className="text-sm font-black text-white leading-tight mt-1">وعي الغذاء</span>
                            <span className="text-sm font-black text-white leading-tight">صلاة</span>
                            <div className="mt-2 w-7 h-px bg-amber-300/60"></div>
                            <span className="text-[9px] font-semibold text-amber-200/90 mt-2">كتيب تطبيقي</span>
                            <BookOpen className="w-5 h-5 text-amber-200 mt-2" strokeWidth={2} />
                          </div>
                        </div>

                        {/* Spine shadow */}
                        <div className="booklet-spine"></div>
                      </div>
                    </div>

                    {/* "Notification" red dot */}
                    <span className="absolute -top-1 -right-1 z-20 w-3 h-3 bg-[#EF4444] rounded-full ring-2 ring-white animate-ping"></span>
                    <span className="absolute -top-1 -right-1 z-20 w-3 h-3 bg-[#EF4444] rounded-full ring-2 ring-white"></span>
                  </button>

                  {/* Caption (acts as the secondary clickable label) */}
                  <button
                    type="button"
                    onClick={() => navigate('/dashboard?tab=booklet')}
                    className="group inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/85 backdrop-blur-sm border border-[#FBBF24]/50 hover:border-[#FBBF24] shadow-md shadow-amber-500/15 hover:shadow-amber-500/35 hover:scale-[1.04] transition-all duration-300"
                    data-testid="booklet-cta-landing-caption"
                    style={{ fontFamily: 'Cairo, sans-serif' }}
                  >
                    <span className="text-xs md:text-sm font-extrabold text-[#1A1D3D] leading-tight">
                      إضغط هنا للحصول على الكتيب التطبيقي
                    </span>
                    <ArrowLeft className="w-4 h-4 text-[#7C3AED] group-hover:-translate-x-1 transition" />
                  </button>
                </div>
              </>
            )}

            {/* Watch Recorded Course Button — placed where the registration deadline banner used to be */}
            <div className="flex justify-center mb-6">
              <button
                type="button"
                onClick={async () => {
                  if (!user) {
                    toast.info('يرجى تسجيل الدخول لمشاهدة الجلسات المُسجَّلة');
                    login();
                    return;
                  }
                  let fresh = user;
                  try {
                    const refreshed = await checkAuth();
                    if (refreshed) fresh = refreshed;
                  } catch (e) { /* ignore */ }
                  if (!fresh) {
                    toast.info('يرجى تسجيل الدخول لمشاهدة الجلسات المُسجَّلة');
                    login();
                    return;
                  }
                  if (fresh.role === 'admin' || fresh.enrolled) {
                    navigate('/watch');
                  } else {
                    toast.error('يجب التسجيل في الكورس لمشاهدة الجلسات المُسجَّلة');
                    const pricingEl = document.getElementById('pricing');
                    if (pricingEl) pricingEl.scrollIntoView({ behavior: 'smooth' });
                  }
                }}
                className="group relative overflow-hidden inline-flex items-center gap-3 md:gap-4 px-6 md:px-8 py-4 md:py-5 rounded-2xl bg-gradient-to-r from-[#1A1D3D] via-[#7C3AED] to-[#EC4899] text-white shadow-xl shadow-purple-500/30 ring-1 ring-white/15 hover:ring-white/40 hover:shadow-pink-500/40 hover:scale-[1.03] active:scale-[0.98] transition-all duration-300"
                data-testid="watch-recorded-button"
                style={{ fontFamily: 'Cairo, sans-serif' }}
              >
                <span className="absolute -inset-1 bg-gradient-to-r from-[#7C3AED] via-[#EC4899] to-[#FBBF24] opacity-30 blur-2xl group-hover:opacity-60 transition-opacity duration-500 -z-10"></span>
                <span className="absolute inset-0 bg-gradient-to-r from-transparent via-white/15 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700"></span>

                <div className="relative w-12 h-12 md:w-14 md:h-14 flex items-center justify-center rounded-full bg-white/15 backdrop-blur-sm ring-2 ring-white/30 flex-shrink-0">
                  <span className="absolute inset-0 rounded-full animate-ping bg-white/20"></span>
                  <Video className="relative w-6 h-6 md:w-7 md:h-7 text-white" />
                </div>

                <div className="relative flex flex-col items-start text-right">
                  <span className="text-[10px] md:text-xs font-bold uppercase tracking-widest opacity-80 leading-none mb-1">
                    تسجيلات الجلسات الأربع
                  </span>
                  <span className="text-base md:text-xl lg:text-2xl font-black leading-tight">
                    شاهد الكورس مُسجَّل
                  </span>
                  <span className="text-[10px] md:text-xs opacity-80 mt-1 leading-tight">
                    مشغّل احترافي مع قائمة تشغيل كاملة
                  </span>
                </div>

                <ChevronLeft className="relative w-5 h-5 md:w-6 md:h-6 text-white/80 group-hover:text-white group-hover:-translate-x-1 transition-all" />
              </button>
            </div>

            {/* 1-year access notice */}
            <div className="flex justify-center mb-12 -mt-2">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/70 backdrop-blur-sm border border-[#7C3AED]/25 shadow-sm" data-testid="watch-access-duration-notice">
                <Clock className="w-4 h-4 text-[#7C3AED] flex-shrink-0" strokeWidth={2.5} />
                <p className="text-xs md:text-sm font-bold text-[#1A1D3D]" style={{ fontFamily: 'Cairo, sans-serif' }}>
                  الكورس متاح لك لمدة <span className="text-[#7C3AED]">سنة كاملة</span>
                </p>
              </div>
            </div>
            <h1 className="mb-12 pb-3 text-4xl sm:text-5xl lg:text-7xl font-black" style={{ fontFamily: 'Cairo, sans-serif', lineHeight: '1.4', background: 'linear-gradient(135deg, #1A1D3D 0%, #7C3AED 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
              وعي الغذاء صلاة
            </h1>

            {/* Main Presenter - Big & Centered */}
            <div className="flex justify-center items-start mb-10">
              {/* Dr. Nawaf */}
              <div className="flex flex-col items-center gap-3">
                <div className="w-28 h-28 md:w-36 md:h-36 lg:w-40 lg:h-40 rounded-full ring-4 ring-[#7C3AED] ring-offset-4 overflow-hidden shadow-xl shadow-purple-500/25">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_c454fa46-2dca-4737-a121-7787b088fb19/artifacts/6m0v4mi8_naw.png"
                    alt="د. نواف الشبلي"
                    className="w-full h-full object-cover"
                    data-testid="presenter-photo-nawaf"
                  />
                </div>
                <span className="text-sm md:text-lg font-bold text-[#1A1D3D]">د. نواف الشبلي</span>
              </div>
            </div>

            {/* Guest - Mystery VIP, identity revealed at course launch */}
            <div className="flex flex-col items-center gap-3 mb-10">
              <div className="relative group">
                {/* Glowing gold ring */}
                <div className="absolute -inset-2 bg-gradient-to-r from-[#FBBF24] via-[#F59E0B] to-[#FBBF24] rounded-full opacity-50 blur-md group-hover:opacity-70 transition-opacity duration-500 animate-pulse"></div>
                <div
                  className="relative w-24 h-24 md:w-28 md:h-28 rounded-full ring-2 ring-[#FBBF24] ring-offset-4 ring-offset-white overflow-hidden shadow-2xl shadow-amber-500/30 bg-gradient-to-br from-[#1A1D3D] via-[#2D1B69] to-[#1A1D3D] flex items-center justify-center"
                  data-testid="presenter-photo-guest"
                >
                  {/* Subtle pattern overlay */}
                  <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_30%,rgba(251,191,36,0.25),transparent_60%)]"></div>
                  {/* Silhouette */}
                  <svg viewBox="0 0 80 80" className="absolute w-full h-full opacity-30" fill="none">
                    <circle cx="40" cy="30" r="14" fill="#FBBF24" />
                    <path d="M14 78 Q14 50 40 50 Q66 50 66 78 Z" fill="#FBBF24" />
                  </svg>
                  {/* Lock + question */}
                  <div className="relative z-10 flex flex-col items-center">
                    <Lock className="w-7 h-7 md:w-9 md:h-9 text-[#FBBF24] drop-shadow-[0_0_12px_rgba(251,191,36,0.6)]" strokeWidth={2.5} />
                    <span className="text-[10px] md:text-xs font-black text-[#FBBF24] mt-0.5 tracking-widest">VIP</span>
                  </div>
                </div>
              </div>
              <div className="text-center">
                <div className="inline-flex items-center gap-2 mt-1 px-3 py-1 rounded-full bg-gradient-to-r from-[#FEF3C7] to-[#FEF3C7] border border-[#FBBF24]/40">
                  <Sparkles className="w-3.5 h-3.5 text-[#D97706]" />
                  <p className="text-sm md:text-base text-[#7C3AED] font-bold" style={{ fontFamily: 'Cairo, sans-serif' }}>مشاركة خاصة من ضيف مُميز</p>
                </div>
                <p className="text-xs md:text-base text-[#4A5078] mt-3 max-w-lg mx-auto leading-relaxed">لفتح طبقات الأكل العاطفي وفهم الرابط الخفي بين المشاعر والجسد</p>
              </div>
            </div>

            {/* Via Zoom Badge - Featured with Animation */}
            <div className="flex justify-center mb-12">
              <div className="relative group">
                {/* Pulsing glow ring */}
                <div className="absolute -inset-3 bg-gradient-to-r from-[#7C3AED] via-[#06B6D4] to-[#7C3AED] rounded-3xl opacity-30 blur-xl animate-zoom-glow"></div>
                <div className="relative flex items-center gap-4 lg:gap-5 p-5 lg:p-6 bg-white rounded-3xl border-2 border-[#E2E8F0] shadow-2xl shadow-purple-500/20 animate-zoom-float">
                  {/* Zoom Icon with beating animation */}
                  <div className="w-16 h-16 lg:w-20 lg:h-20 bg-gradient-to-br from-[#7C3AED] to-[#06B6D4] rounded-2xl flex items-center justify-center shadow-xl shadow-cyan-500/30 animate-zoom-beat">
                    <svg viewBox="0 0 24 24" className="w-9 h-9 lg:w-12 lg:h-12 text-white" fill="currentColor">
                      <path d="M4 4h10a2 2 0 0 1 2 2v6.5l4-2.5v8l-4-2.5V18a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z"/>
                    </svg>
                  </div>
                  <div>
                    <p className="text-sm lg:text-base font-bold uppercase tracking-wider text-[#06B6D4]">٤ جلسات مباشرة</p>
                    <p className="text-2xl lg:text-3xl font-black text-[#1A1D3D]" style={{ fontFamily: 'Cairo, sans-serif' }}>عبر Zoom</p>
                  </div>
                  {/* Live dot indicator */}
                  <div className="absolute -top-2 -left-2 flex items-center gap-1 bg-red-500 text-white text-[10px] font-bold px-2 py-1 rounded-full shadow-lg">
                    <span className="w-2 h-2 bg-white rounded-full animate-ping-slow"></span>
                    LIVE
                  </div>
                </div>
              </div>
            </div>

            {/* Eid Al-Adha Special Offer Banner */}
            <div className="flex justify-center mb-6">
              <div className="relative overflow-hidden bg-gradient-to-r from-[#7C3AED] via-[#EC4899] to-[#06B6D4] rounded-2xl px-6 py-4 shadow-xl shadow-purple-500/25 border border-white/20 animate-zoom-float" style={{ animationDuration: '4s' }} data-testid="eid-offer-banner">
                <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAiIGhlaWdodD0iMjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGNpcmNsZSBjeD0iMTAiIGN5PSIxMCIgcj0iMS41IiBmaWxsPSJyZ2JhKDI1NSwyNTUsMjU1LDAuMSkiLz48L3N2Zz4=')] opacity-60"></div>
                <p className="relative text-base md:text-xl font-bold text-white text-center leading-relaxed" style={{ fontFamily: 'Cairo, sans-serif' }}>
                  عرض خاص بمناسبة عيد الأضحى 2026 — عام حصان النار ✨🐎
                </p>
              </div>
            </div>

            {/* Watch Recorded Course Button */}
          </div>
        </div>
      </section>

      {/* Session Dates Section */}
      <section id="course" className="py-16 md:py-24 px-4 md:px-12 lg:px-24 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <p className="overline mb-4">مواعيد الجلسات</p>
            <h2 className="heading-2 text-2xl lg:text-4xl">٤ جلسات مباشرة عبر Zoom</h2>
            <p className="body-text mt-3">مدة الجلسة من ساعتين إلى ساعتين ونصف</p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 lg:gap-6 max-w-3xl mx-auto mb-12">
            {sessionDates.map((session, index) => (
              <div key={index} className="bg-gradient-to-br from-[#F0F4FF] to-[#FCE7F3] rounded-2xl p-5 text-center border border-[#E2E8F0] cursor-pointer transition-all duration-300 hover:bg-gradient-to-br hover:from-[#7C3AED] hover:to-[#EC4899] hover:border-transparent hover:shadow-xl hover:shadow-purple-500/25 hover:scale-105 group">
                <Calendar className="w-6 h-6 text-[#7C3AED] mx-auto mb-2 group-hover:text-white transition-colors duration-300" />
                <p className="text-lg font-bold text-[#1A1D3D] group-hover:text-white transition-colors duration-300" style={{ fontFamily: 'Cairo, sans-serif' }}>{session.date}</p>
                <p className="text-sm text-[#8B92B3] group-hover:text-white/80 transition-colors duration-300">{session.day}</p>
              </div>
            ))}
          </div>

          <div className="text-center space-y-3">
            <div className="inline-flex items-center gap-2 bg-gradient-to-r from-[#7C3AED]/10 to-[#EC4899]/10 rounded-full px-6 py-3 border border-[#7C3AED]/20 cursor-pointer transition-all duration-300 hover:from-[#7C3AED] hover:to-[#EC4899] hover:shadow-lg hover:shadow-purple-500/25 hover:scale-105 group">
              <Clock className="w-5 h-5 text-[#7C3AED] group-hover:text-white transition-colors duration-300" />
              <span className="font-semibold text-[#1A1D3D] group-hover:text-white transition-colors duration-300">كل يوم الساعة ٣ ظهراً بتوقيت غرينتش</span>
            </div>
            <div className="flex flex-wrap justify-center gap-3 lg:gap-4">
              {[
                { zone: "سوريا", time: "٦ مساءً" },
                { zone: "مكة المكرمة", time: "٦ مساءً" },
                { zone: "القدس", time: "٦ مساءً" },
                { zone: "العراق", time: "٦ مساءً" },
                { zone: "دبي", time: "٧ مساءً" },
                { zone: "الجزائر", time: "٤ عصراً" },
                { zone: "المغرب", time: "٤ عصراً" }
              ].map((tz, index) => (
                <div key={index} className="bg-white rounded-lg px-3 py-2 border border-[#E2E8F0] text-center shadow-sm cursor-pointer transition-all duration-300 hover:bg-gradient-to-br hover:from-[#7C3AED] hover:to-[#EC4899] hover:border-transparent hover:shadow-lg hover:shadow-purple-500/20 hover:scale-105 group">
                  <p className="text-xs text-[#8B92B3] group-hover:text-white/80 transition-colors duration-300">بتوقيت {tz.zone}</p>
                  <p className="text-sm font-bold text-[#7C3AED] group-hover:text-white transition-colors duration-300">{tz.time}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Countdown Timer Section */}
      <CountdownTimer />

      {/* Paragraphs Section */}
      <section className="py-16 md:py-28 px-4 md:px-12 lg:px-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[#0F1129] via-[#1A1D3D] to-[#0F1129]"></div>
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGNpcmNsZSBjeD0iMzAiIGN5PSIzMCIgcj0iMS41IiBmaWxsPSJyZ2JhKDI1NSwyNTUsMjU1LDAuMDMpIi8+PC9zdmc+')] opacity-60"></div>
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-[#7C3AED]/10 rounded-full blur-[150px] pointer-events-none"></div>
        <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-[#EC4899]/8 rounded-full blur-[150px] pointer-events-none"></div>

        <div className="relative max-w-4xl mx-auto">
          <div className="text-center mb-12 md:mb-16">
            <div className="inline-flex items-center gap-2 bg-white/5 backdrop-blur-sm rounded-full px-5 py-2 mb-5 border border-white/10">
              <span className="w-2 h-2 bg-[#EC4899] rounded-full animate-pulse"></span>
              <span className="text-xs font-semibold text-white/60 tracking-wider uppercase">محاور الكورس</span>
            </div>
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-white mb-6" style={{ fontFamily: 'Cairo, sans-serif' }}>
              في هذا الكورس سنفهم أنواع الأكل
            </h2>
            <div className="max-w-2xl mx-auto bg-white/[0.04] backdrop-blur-sm rounded-2xl border border-white/[0.08] px-6 py-6 md:px-8 md:py-7" style={{ fontFamily: 'Cairo, sans-serif' }}>
              <ul className="space-y-2.5 text-white/80 text-sm md:text-base lg:text-lg leading-relaxed">
                {[
                  "الأكل عن جوع",
                  "الأكل عن شعور",
                  "الأكل الاجتماعي",
                  "الأكل الواعي",
                  "الأكل لهدف (صحة / قوة / شكل)"
                ].map((t, i) => (
                  <li key={i} className="flex items-center gap-2.5">
                    <span className="flex-shrink-0 w-1.5 h-1.5 bg-gradient-to-br from-[#EC4899] to-[#7C3AED] rounded-full"></span>
                    <span>{t}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-5 pt-5 border-t border-white/[0.08]">
                <p className="text-white/70 text-sm md:text-base leading-loose">
                  وكيف أن <span className="text-white font-bold">نيتك أثناء الأكل</span>
                  <br/>
                  تُغيّر <span className="text-white font-bold">استجابة جسدك</span> بالكامل.
                </p>
              </div>
            </div>
          </div>

          <div className="space-y-5 md:space-y-6">
            {[
              { title: "نبدأ من حيث يتوقف الجميع", text: "لا نتوقف عند الأعراض والهضم والأنظمة، بل نستخدم الطعام كبوابة لفهم ما وراءه.", color: "from-[#7C3AED] to-[#A78BFA]", num: "01" },
              { title: "لماذا نفس الطعام ينجح ويفشل؟", text: "لأن الجسد لا يقرأ الطعام فقط، بل يقرأ حالتك، ووعيك، وظروفك.", color: "from-[#EC4899] to-[#F9A8D4]", num: "02" },
              { title: "الأكل ليس جسديًا فقط", text: "الأكل يحدث على طبقات: جسد، مشاعر، فكر، ووعي — وكل طبقة تستقبل الطعام بشكل مختلف.", color: "from-[#F97316] to-[#FDBA74]", num: "03" },
              { title: "سنكشف «النمط المتكرر» (الكارما)", text: "حيث تتكرر نفس الأعراض رغم تغيير الأنظمة — نُعيد تشكيل الاختيار من الداخل قبل أن يحدث الفعل.", color: "from-[#06B6D4] to-[#67E8F9]", num: "04" },
              { title: "وراء كل مرض… منظومة", text: "حالة نفسية، ضغط، بيئة، مجتمع، وقوانين كونية دقيقة تربط الإنسان بما يأكل وما يشعر.", color: "from-[#EF4444] to-[#FCA5A5]", num: "05" },
              { title: "من تناقض المدارس إلى وضوح الرؤية", text: "كيف تستفيد من كل الأنظمة الغذائية دون أن تتبع أو تتعصب، وتفهم أين يقع الجزء وأين يقع الكل.", color: "from-[#7C3AED] to-[#EC4899]", num: "06" },
              { title: "نصل إلى الجذر", text: "حيث الوعي، والتنفس، والنية — وكيف تؤثر القوانين الكونية والحالة الداخلية في الجسد، وحتى في الـ DNA، لتُعيد التوازن من الداخل.", color: "from-[#10B981] to-[#6EE7B7]", num: "07" }
            ].map((item, index) => (
              <div 
                key={index}
                className="group relative bg-white/[0.04] backdrop-blur-sm rounded-2xl border border-white/[0.08] p-5 md:p-7 hover:bg-white/[0.08] hover:border-white/[0.15] transition-all duration-500 opacity-0 animate-fade-in"
                style={{ animationDelay: `${index * 0.1}s`, animationFillMode: 'forwards' }}
              >
                {/* Accent line */}
                <div className={`absolute top-0 right-0 w-1 h-full bg-gradient-to-b ${item.color} rounded-r-2xl opacity-60 group-hover:opacity-100 transition-opacity duration-500`}></div>
                
                <div className="flex items-start gap-4 md:gap-5">
                  {/* Bullet number */}
                  <div className={`flex-shrink-0 w-10 h-10 md:w-12 md:h-12 bg-gradient-to-br ${item.color} rounded-xl flex items-center justify-center shadow-lg opacity-80 group-hover:opacity-100 group-hover:scale-110 transition-all duration-500`}>
                    <span className="text-white font-black text-sm md:text-base" style={{ fontVariantNumeric: 'tabular-nums' }}>{item.num}</span>
                  </div>
                  
                  {/* Title + Text */}
                  <div className="flex-1 min-w-0" style={{ fontFamily: 'Cairo, sans-serif' }}>
                    <h3 className={`bg-gradient-to-r ${item.color} bg-clip-text text-transparent font-black text-base md:text-lg lg:text-xl leading-snug mb-2.5`}>
                      {item.title}
                    </h3>
                    <p className="text-white/75 group-hover:text-white/95 transition-colors duration-500 text-sm md:text-base lg:text-lg leading-[2]">
                      {item.text}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Course Description Section */}
      <section className="py-16 md:py-24 px-4 md:px-12 lg:px-24 bg-gradient-to-br from-[#F0F4FF] via-[#FCE7F3] to-[#FEF3C7]">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-24 items-center">
            <div>
              <p className="overline mb-4">عن الدورة</p>
              <h2 className="heading-2 mb-6 text-2xl lg:text-4xl">وعي الغذاء صلاة</h2>
              <p className="body-large mb-6 text-base lg:text-lg leading-loose">
                اعرف أنّ أولويات التغذية: غذاء الرُّوح التي هي حقيقتك، واعرف أنَّها هي نفسها القوة الداخلية للكون. دع تفكيرك يصل إلى الحقيقة. حلّل نفسك واكتشف طبقات الوعي المتعددة. عليك الوصول إلى القلب ذاته — إلى الطبقة الأخيرة، طبقة الغبطة — وعندها تستطيع الوصول إلى اعتبار نفسك: جسم سليم في عقل سليم، وعقل سليم في جسم سليم.
              </p>
              
              <div className="space-y-3 mb-8">
                {[
                  "فتح طبقات الأكل العاطفي",
                  "فهم الرابط الخفي بين المشاعر والجسد",
                  "تطوير وعي عميق بعلاقتك مع الطعام"
                ].map((benefit, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <div className="w-6 h-6 bg-gradient-to-br from-[#7C3AED] to-[#EC4899] rounded-full flex items-center justify-center flex-shrink-0 shadow-lg shadow-purple-500/20">
                      <Star className="w-3 h-3 text-white" />
                    </div>
                    <span className="text-[#4A5078]">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <div className="aspect-video rounded-3xl overflow-hidden shadow-2xl shadow-purple-500/20 border-4 border-white">
                <img 
                  src="https://images.unsplash.com/photo-1569246294372-ed319c674f14?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDB8MHwxfHNlYXJjaHwxfHx2ZWdhbiUyMHBsYW50JTIwYmFzZWQlMjBmb29kJTIwZnJlc2glMjB2ZWdldGFibGVzJTIwZnJ1aXRzJTIwYm93bHMlMjBjb2xvcmZ1bHxlbnwwfHx8fDE3NzcwNTkwNDZ8MA&ixlib=rb-4.1.0&q=85"
                  alt="التغذية السليمة - نظام نباتي"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 md:py-24 px-4 md:px-12 lg:px-24 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <p className="overline mb-4">ماذا ستختبر</p>
            <h2 className="heading-2 text-2xl lg:text-4xl">رحلة تحوّل حقيقية</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => {
              const colors = [
                'from-[#7C3AED] to-[#A78BFA]',
                'from-[#EC4899] to-[#F9A8D4]',
                'from-[#06B6D4] to-[#67E8F9]',
                'from-[#F97316] to-[#FDBA74]'
              ];
              return (
                <div 
                  key={index} 
                  className="card-marketing opacity-0 animate-fade-in bg-white/80 backdrop-blur-sm"
                  style={{ animationDelay: `${index * 0.1}s`, animationFillMode: 'forwards' }}
                >
                  <div className={`w-14 h-14 bg-gradient-to-br ${colors[index]} rounded-2xl flex items-center justify-center mb-5 shadow-lg`}>
                    <div className="text-white">{feature.icon}</div>
                  </div>
                  <h3 className="heading-4 mb-2">{feature.title}</h3>
                  <p className="text-small">{feature.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Reviews Section */}
      <ReviewsSection user={user} />

      {/* Pricing Section */}
      <section id="pricing" className="py-16 md:py-24 px-4 md:px-12 lg:px-24 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <p className="overline mb-4">الاستثمار</p>
            <h2 className="heading-2 text-2xl lg:text-4xl">استثمر في وعيك</h2>
          </div>
          
          <div className="max-w-lg mx-auto">
            <div className="bg-white rounded-3xl shadow-2xl shadow-purple-500/15 overflow-hidden border-2 border-[#E2E8F0] relative">
              <div className="absolute inset-0 bg-gradient-to-br from-[#7C3AED] via-[#EC4899] to-[#F97316] opacity-10 pointer-events-none"></div>
              
              {/* Price Header */}
              <div className="bg-gradient-to-r from-[#7C3AED] via-[#EC4899] to-[#F97316] p-8 text-center relative overflow-hidden">
                <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGNpcmNsZSBjeD0iMjAiIGN5PSIyMCIgcj0iMiIgZmlsbD0icmdiYSgyNTUsMjU1LDI1NSwwLjEpIi8+PC9zdmc+')] opacity-50"></div>
                <div className="relative">
                  <h3 className="text-xl font-semibold text-white/90 mb-2">وعي الغذاء صلاة</h3>
                  <div className="flex items-center justify-center gap-2">
                    <span className="text-5xl lg:text-7xl font-black text-white" style={{ fontFamily: 'Cairo, sans-serif' }}>
                      {courseInfo?.price || "499"}
                    </span>
                    <span className="text-xl font-medium text-white/80">{courseInfo?.currency || "دولار"}</span>
                  </div>
                  <p className="text-white/70 mt-2">دفعة واحدة</p>
                </div>
              </div>
              
              {/* Features List */}
              <div className="p-6 lg:p-8 relative">
                <ul className="space-y-4 mb-8">
                  {[
                    "٤ جلسات مباشرة عبر Zoom",
                    "مدة الجلسة من ساعتين إلى ساعتين ونصف",
                    "مشاركة خاصة مع ضيف ومعالج"
                  ].map((feature, index) => (
                    <li key={index} className="flex items-center gap-3">
                      <div className="w-6 h-6 bg-gradient-to-br from-[#10B981] to-[#34D399] rounded-full flex items-center justify-center flex-shrink-0 shadow-lg shadow-emerald-500/20">
                        <Check className="w-3.5 h-3.5 text-white" />
                      </div>
                      <span className="text-[#4A5078]">{feature}</span>
                    </li>
                  ))}
                </ul>

                {/* Access Duration Notice */}
                <div className="mb-6 -mt-2 px-4 py-3 rounded-xl bg-gradient-to-r from-[#FEF3C7] via-[#FFE4E6] to-[#FCE7F3] border border-[#EC4899]/30 flex items-center gap-3" data-testid="access-duration-notice">
                  <Clock className="w-5 h-5 text-[#7C3AED] flex-shrink-0" strokeWidth={2.5} />
                  <p className="text-xs md:text-sm font-bold text-[#1A1D3D] leading-snug" style={{ fontFamily: 'Cairo, sans-serif' }}>
                    الكورس متاح لك لمدة <span className="text-[#7C3AED]">سنة كاملة</span> من تاريخ الدفع.
                  </p>
                </div>

                {/* Emotional Call to Action */}
                <div className="text-center mb-8 py-6 border-t border-b border-[#E2E8F0]">
                  <p className="text-[#4A5078] text-sm md:text-base leading-loose" style={{ fontFamily: 'Cairo, sans-serif' }}>
                    إذا شعرت بأن شيئًا فيك<br/>
                    <span className="text-[#7C3AED] font-semibold">انفتح الآن</span><br/>
                    لا تحاول تفسيره<br/>
                    <span className="text-[#EC4899] font-bold text-base md:text-lg">هذا هو النداء</span>
                  </p>
                </div>
                
                {/* If already enrolled — show big "Already enrolled" panel INSTEAD of payment buttons */}
                {user?.enrolled ? (
                  <div data-testid="already-enrolled-panel" className="rounded-2xl border-2 border-emerald-400 bg-gradient-to-br from-emerald-50 via-white to-emerald-50 p-6 text-center">
                    <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-gradient-to-br from-emerald-500 to-teal-600 shadow-lg shadow-emerald-500/30 mb-3">
                      <Check className="w-7 h-7 text-white" strokeWidth={3} />
                    </div>
                    <h3 className="text-lg font-black text-emerald-800 mb-1">أنت مُسجَّل في الدورة ✓</h3>
                    <p className="text-sm text-emerald-700 mb-4 leading-relaxed">
                      تم تأكيد دفعتك بنجاح. لا حاجة للدفع مرة أخرى.
                      <br/>
                      روابط Zoom ستصلك في صندوق الوارد.
                    </p>
                    <Button
                      onClick={() => navigate('/dashboard')}
                      className="w-full bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white py-5 text-base rounded-xl shadow-lg shadow-emerald-500/25"
                      data-testid="already-enrolled-goto-inbox"
                    >
                      <Inbox className="w-5 h-5 ml-2" />
                      اذهب إلى صندوق الوارد
                      <ChevronLeft className="w-5 h-5 mr-2" />
                    </Button>
                  </div>
                ) : (
                <>
                {/* Payment Options — Stripe + PayPal */}
                <div className="space-y-3" data-testid="payment-options">
                  <Button
                    onClick={handleEnrollClick}
                    disabled={checkoutLoading || paypalLoading}
                    className="w-full bg-gradient-to-r from-[#7C3AED] to-[#EC4899] hover:from-[#6D28D9] hover:to-[#DB2777] text-white py-6 text-lg rounded-xl shadow-lg shadow-purple-500/25 disabled:opacity-60 disabled:cursor-not-allowed"
                    data-testid="pricing-stripe-button"
                  >
                    <CreditCard className="w-5 h-5 ml-2" />
                    {checkoutLoading ? "جارٍ التحويل إلى الدفع..." : "ادفع ببطاقة الائتمان"}
                    <ChevronLeft className="w-5 h-5 mr-2" />
                  </Button>

                  <div className="relative flex items-center justify-center my-2">
                    <span className="absolute inset-x-0 h-px bg-[#E2E8F0]"></span>
                    <span className="relative bg-white px-3 text-xs text-[#8B92B3]">أو</span>
                  </div>

                  <Button
                    onClick={handlePayPalClick}
                    disabled={checkoutLoading || paypalLoading}
                    className="w-full bg-[#FFC439] hover:bg-[#F0B72D] text-[#003087] py-6 text-lg rounded-xl shadow-lg shadow-yellow-500/20 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2 font-bold"
                    data-testid="pricing-paypal-button"
                  >
                    {paypalLoading ? (
                      "جارٍ التحويل إلى PayPal..."
                    ) : (
                      <>
                        <span>ادفع عبر</span>
                        <span className="text-[#003087] font-black tracking-tight">
                          Pay<span className="text-[#009CDE]">Pal</span>
                        </span>
                      </>
                    )}
                  </Button>
                </div>
                
                <p className="text-center text-xs text-[#8B92B3] mt-4">
                  دفع آمن — Visa / Mastercard / Apple Pay / Google Pay / PayPal
                </p>
                </>
                )}
              </div>
            </div>

            {/* Company Info — directly under the price card */}
            <div className="mt-8 max-w-md mx-auto text-center space-y-2 px-4 py-5 rounded-2xl bg-white/60 backdrop-blur-sm border border-[#E2E8F0]" data-testid="pricing-company-info">
              <p className="text-base md:text-lg font-bold text-[#1A1D3D]" style={{ fontFamily: 'Cairo, sans-serif' }}>
                شركة إينر بريدج
              </p>
              <p className="text-xs md:text-sm text-[#4A5078] font-medium" dir="ltr">
                Licensed in Dubai, U.A.E. <span className="text-[#8B92B3] mx-1">|</span> License #2646553.01
              </p>
              <p className="text-[11px] md:text-xs text-[#8B92B3] pt-1" dir="ltr">
                © 2026 The Inner Bridge (Innovation Lifestyle Consultancy L.L.C-FZ). All rights reserved.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-10 md:py-16 px-4 md:px-12 lg:px-24 border-t border-[#E2E8F0] bg-white">
        <div className="max-w-7xl mx-auto">
          {/* Contact Info Block */}
          <div className="bg-gradient-to-br from-[#F0F4FF] via-white to-[#FCE7F3] border border-[#E2E8F0] rounded-2xl p-6 md:p-8 mb-6 shadow-sm" data-testid="footer-contact-block">
            {/* Primary contact */}
            <div className="flex flex-col items-center justify-center gap-4 pb-6 mb-6 border-b border-[#E2E8F0]/70 text-center">
              <div className="w-12 h-12 flex-shrink-0 bg-gradient-to-br from-[#7C3AED] to-[#EC4899] rounded-xl flex items-center justify-center shadow-lg shadow-purple-500/20">
                <MessageCircle className="w-6 h-6 text-white" />
              </div>
              <div className="text-center">
                <p className="text-xs md:text-sm font-semibold text-[#7C3AED] mb-1" style={{ fontFamily: 'Cairo, sans-serif' }}>
                  للتواصل والاستفسار على رقم الواتس-آب
                </p>
                <a
                  href="https://wa.me/963988242654"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block text-lg md:text-xl font-black text-[#1A1D3D] hover:text-[#7C3AED] transition-colors tracking-wide"
                  dir="ltr"
                  data-testid="footer-primary-whatsapp"
                >
                  +963 988 242 654
                </a>
                <p className="text-xs md:text-sm text-[#4A5078] mt-2" style={{ fontFamily: 'Cairo, sans-serif' }}>
                  كنان واكد
                </p>
                <p className="text-[11px] md:text-xs text-[#8B92B3]" style={{ fontFamily: 'Cairo, sans-serif' }}>
                  مدير قسم البرمجة والذكاء الاصطناعي
                </p>
              </div>
            </div>

            {/* Customer service regional numbers */}
            <div>
              <p className="text-center text-sm md:text-base font-bold text-[#1A1D3D] mb-5" style={{ fontFamily: 'Cairo, sans-serif' }}>
                أرقام خدمة العملاء — واتس آب
              </p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
                {[
                  { region: 'الأمريكيتين', number: '+17808627900', display: '+1 780 862 7900' },
                  { region: 'أوروبا', number: '+33660716479', display: '+33 660 716 479' },
                  { region: 'دمشق', number: '+963933492642', display: '+963 933 492 642' },
                  { region: 'دول الخليج', number: '+966558604875', display: '+966 558 604 875' },
                ].map((item) => (
                  <a
                    key={item.number}
                    href={`https://wa.me/${item.number.replace('+', '')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`footer-cs-${item.number.replace('+', '')}`}
                    className="group bg-white border border-[#E2E8F0] hover:border-[#7C3AED]/50 rounded-xl px-3 py-3 md:px-4 md:py-4 text-center transition-all hover:shadow-md hover:shadow-purple-500/10 hover:-translate-y-0.5"
                  >
                    <div className="flex items-center justify-center gap-1.5 mb-2">
                      <Phone className="w-3.5 h-3.5 text-[#7C3AED]" />
                      <span className="text-xs md:text-sm font-bold text-[#1A1D3D]" style={{ fontFamily: 'Cairo, sans-serif' }}>
                        {item.region}
                      </span>
                    </div>
                    <span dir="ltr" className="block text-xs md:text-[13px] font-semibold text-[#4A5078] group-hover:text-[#7C3AED] transition-colors tracking-tight">
                      {item.display}
                    </span>
                  </a>
                ))}
              </div>
            </div>
          </div>

          {/* No refund notice */}
          <div className="bg-[#FEF2F2] border border-[#EF4444]/20 rounded-xl px-4 py-3 mb-6 text-center">
            <p className="text-xs md:text-sm text-[#EF4444] font-semibold">
              جميع المبالغ المدفوعة غير قابلة للاسترداد نظرًا للطبيعة الرقمية للمنتج. يُرجى قراءة <a href="/legal/refund" className="underline hover:text-[#DC2626]">سياسة الاسترداد</a> قبل الشراء.
            </p>
          </div>
          {/* Brand + legal links row (moved below no-refund notice) */}
          <div className="flex flex-col md:flex-row items-center md:items-start justify-between gap-6 mb-6 pt-6 border-t border-[#E2E8F0]">
            <div className="flex items-center gap-2">
              <div className="w-9 h-9 bg-gradient-to-br from-[#7C3AED] to-[#EC4899] rounded-xl flex items-center justify-center shadow-lg shadow-purple-500/20">
                <Sparkles className="w-5 h-5 text-white" />
              </div>
              <span className="font-bold text-[#1A1D3D]" style={{ fontFamily: 'Cairo, sans-serif' }}>
                وعي الغذاء صلاة
              </span>
            </div>
            <div className="flex flex-wrap justify-center gap-x-6 gap-y-3 text-sm text-[#4A5078]">
              <a href="/legal/privacy" className="hover:text-[#7C3AED] transition-colors font-medium">سياسة الخصوصية</a>
              <a href="/legal/terms" className="hover:text-[#7C3AED] transition-colors font-medium">شروط الاستخدام</a>
              <a href="/legal/refund" className="hover:text-[#7C3AED] transition-colors font-medium">سياسة الاسترداد</a>
            </div>
          </div>
          {/* Bottom — short brand line */}
          <div className="text-center pt-6 border-t border-[#E2E8F0]/70">
            <p className="text-sm text-[#8B92B3]">
              © {new Date().getFullYear()} وعي الغذاء صلاة. جميع الحقوق محفوظة.
            </p>
          </div>
        </div>
      </footer>
      {/* Scroll to Top Lightning Button */}
      <button
        onClick={() => document.getElementById('hero').scrollIntoView({ behavior: 'smooth' })}
        className="fixed bottom-6 left-6 z-50 w-14 h-14 bg-gradient-to-br from-[#7C3AED] to-[#EC4899] rounded-full flex items-center justify-center shadow-2xl shadow-purple-500/40 hover:scale-110 transition-transform duration-200 group"
        data-testid="scroll-to-top-btn"
        aria-label="العودة إلى الأعلى"
      >
        <ArrowUp className="w-6 h-6 text-white group-hover:animate-pulse" />
      </button>
    </div>
  );
};

export default LandingPage;
